import React, { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { createPortal } from 'react-dom';

// Movido de finance/moduleComponents — dropdown mais robusto do repo (portal, hover state).
const DropdownCustomDark = ({
  options = [],
  selectedValue = '',
  placeholder = 'Select',
  width,
  onSelect = () => {},
  onToggle = () => {},
  disabled = false,
  showAllOption = false,
  allOptionText = 'All',
  allOptionValue = '',
  position = 'right',
  zIndex = 'z-10',
  showRadio = false,
  dropdownClassMenu = 'bg-gray-700 shadow-lg ring-1 ring-black ring-opacity-5',
  dropdownClassOption = 'cursor-pointer px-3 py-1.5 text-xs hover:bg-gray-600 text-white flex items-center',
  dropdownClassAllOption = '',
  dropdownClassEmpty = 'px-3 py-1.5 text-xs text-gray-300 italic',
  dropdownClassContainer = '',
  dropdownClassName = '',
  usePortal = false,
  portalRef = null,
  triggerBgOpacity = 30,
  /** When true, label wraps in the trigger instead of single-line ellipsis (truncate). */
  allowLabelWrap = false,
}) => {
  const opacity = typeof triggerBgOpacity === 'number' ? Math.min(100, Math.max(0, triggerBgOpacity)) / 100 : 0.3;
  const [isOpen, setIsOpen] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const triggerBgStyle = { backgroundColor: `rgb(107 114 128 / ${isHover ? 1 : opacity})` };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        (dropdownRef.current && dropdownRef.current.contains(event.target)) ||
        (buttonRef.current && buttonRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    const newState = !isOpen;
    setIsOpen(newState);
    onToggle(newState);
  };

  const handleSelect = (value) => {
    onSelect(value);
    setIsOpen(false);
  };

  const getPortalStyle = () => {
    const ref = portalRef && portalRef.current ? portalRef : buttonRef;
    if (!ref.current) return {};
    const rect = ref.current.getBoundingClientRect();
    return {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY + 4}px`,
      left: `${rect.left + window.scrollX}px`,
      zIndex: 8,
      minWidth: rect.width,
    };
  };

  const menuContent = (
    <div
      ref={dropdownRef}
      className={`rounded-md max-h-48 overflow-y-auto custom-scrollbar-dark ${dropdownClassMenu} ${dropdownClassContainer} ${dropdownClassName}`}
      style={usePortal ? {} : { position: 'absolute', top: '100%', [position]: 0, marginTop: '0.25rem', width: width || '100%' }}
    >
      {showAllOption && (
        <div
          onClick={() => handleSelect(allOptionValue)}
          className={`${dropdownClassOption} ${selectedValue === allOptionValue ? 'text-green-400' : ''} ${dropdownClassAllOption}`}
        >
          {showRadio && (
            <input
              type="radio"
              checked={selectedValue === allOptionValue}
              readOnly
              className="mr-2 accent-green-400"
            />
          )}
          {allOptionText}
        </div>
      )}
      {options.map((option, index) => {
        const optionValue = typeof option === 'object' ? option.value : option;
        const optionLabel = typeof option === 'object' ? option.label : option;
        const isSelected = selectedValue === optionValue;
        return (
          <div
            key={index}
            onClick={() => handleSelect(optionValue)}
            className={`${dropdownClassOption} ${isSelected ? 'text-green-400' : ''}`}
          >
            {showRadio && (
              <input
                type="radio"
                checked={isSelected}
                readOnly
                className="mr-2 accent-green-400"
              />
            )}
            {optionLabel}
          </div>
        );
      })}
      {options.length === 0 && (
        <div className={dropdownClassEmpty}>
          No options available
        </div>
      )}
    </div>
  );

  const getButtonLabel = () => {
    if (showAllOption && String(selectedValue ?? '') === String(allOptionValue)) {
      return allOptionText;
    }
    const sv = selectedValue;
    if (sv === '' || sv === null || sv === undefined) {
      return placeholder;
    }
    const opt = options.find((o) => (typeof o === 'object' ? o.value : o) === sv);
    if (opt && typeof opt === 'object' && opt.label != null) return opt.label;
    return String(sv);
  };

  return (
    <div className="relative" ref={buttonRef}>
      <button
        type="button"
        onClick={handleToggle}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        disabled={disabled}
        style={disabled ? undefined : triggerBgStyle}
        className={`inline-flex justify-between gap-1 ${allowLabelWrap ? 'items-start' : 'items-center'} ${width || 'w-full'} rounded-md border-0 px-2 py-1 text-xs text-white focus:outline-none focus:ring-1 focus:ring-white focus:ring-opacity-30 ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'cursor-pointer'}`}
      >
        <span
          className={
            allowLabelWrap
              ? 'min-w-0 flex-1 text-left leading-tight break-words'
              : 'truncate'
          }
        >
          {getButtonLabel()}
        </span>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 text-white opacity-70 transition-transform ${allowLabelWrap ? 'mt-0.5' : ''} ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        usePortal
          ? createPortal(
              <div style={getPortalStyle()}>{menuContent}</div>,
              document.body
            )
          : menuContent
      )}
    </div>
  );
};

export default DropdownCustomDark;
